<template>
    <IonLabel class="hint">
        Die monatliche Differenz zu den Vertragsstunden geht ins Arbeitszeitkonto ein. Der aktuellen Stand und der historische Verlauf des AZKs wird hier angezeigt:
    </IonLabel>
    <Skeleton v-if="initialDataLoader.pending.value" :items="[`${APEX_SKELETON_WITH_SUMMARY}`]"/>
    <template v-else>
        <div class="boxed">
            <ApexChartValueLine :values="[
                {
                    label: 'AZK',
                    value: monthRanger.selectedRenderSet.value.value,
                    formatter: value => formatter(value as number)
                },
                {
                    label: 'Stand',
                    value: monthRanger.selectedRenderSet.value.month ? date.formatKapitelDate(date.endOfMonth(monthRanger.selectedRenderSet.value.month)) : '?'
                },
            ]" />
            <ApexChartWrapper :options="apexOptions"></ApexChartWrapper>
        </div>
    </template>
</template>

<script setup lang="ts">
import {computed, PropType, Ref, ref} from 'vue';
import {CalendarMonth} from "@/helper/calendar/calendarMonth";
import {DataLoader} from "@/helper/dataLoader";

import Skeleton from "@/components/Skeleton.vue";
import {Employee, EmployeeMonthSummary, EmployeePlannedAbsenceConstraints} from "@/graphql/generated/graphql";

import {kapitelDateString, kapitelMonthString} from '@/graphql/kapitelTypes';
import {useEmployeeStore} from "@/store/employee";
import {ApexMonthRanger, MonthKPIApexMonthRangerFactory} from "@/helper/apexMonthRanger";
import {APEX_HEIGHT, APEX_SKELETON_WITH_SUMMARY, generateApexOptions} from "@/helper/apexchart";
import month from "@/helper/datetime/month";
import {floatFormat} from "@/helper/amountFormatter";
import date from "@/helper/datetime/date";
import ApexChartValueLine from "@/views/components/ApexChartValueLine.vue";
import ApexChartWrapper from "@/views/components/ApexChartWrapper.vue";
import * as money from "@/helper/money";
import {getRandomArrayElement, toMap} from "@/helper/array";
import {fetchEmployeeMonthSummaries} from "@/helper/employeeMonthSummary";
import {fetchEmployeePlannedAbsenceConstraintsHistory} from "@/helper/employeePlannedAbsenceConstraints";
import {isSet, nullish} from "@/helper/isset";
import {IonLabel} from "@ionic/vue";

// props
const props = defineProps({
    selectedMonth: {
        type: String as PropType<kapitelMonthString>,
        required: true,
    }
});

// loader
const initialDataLoader = new DataLoader();

const formatter = (v: number|nullish) => isSet(v) ? (floatFormat(v) + 'h') : '-'

/**
 * month range & selection
 */
const monthRanger = MonthKPIApexMonthRangerFactory({
    selected: props.selectedMonth,
    renderer: (calendarMonth: CalendarMonth) => {
        return {
            calendarMonth,
            value: constraintsMap.value.get(calendarMonth.getId())?.aZK ?? null,
            month: calendarMonth.getId()
        }
    }
})

/**
 * raw data
 */
const constraints = ref(<EmployeePlannedAbsenceConstraints[]>[])
const constraintsMap = computed(() => toMap(constraints.value, (c) => c.validMonth))
initialDataLoader.add(async () => constraints.value = await fetchEmployeePlannedAbsenceConstraintsHistory(monthRanger.min, monthRanger.max))


const employee : Ref<Employee | undefined> = ref(undefined)
initialDataLoader.add(async () =>  employee.value = await useEmployeeStore().getEmployee())

const apexOptions = computed(() => {
    const monthLabels = monthRanger.getMonthLabels()

    return generateApexOptions(
        monthRanger,
        {
            series: [
                {
                    name: "Arbeitszeitkonto",
                    data: monthRanger.allMonthsRenderSets.map(rs => rs.value.value),
                },
            ],
            chart: { type: 'area' },
            yaxis: { labels: { formatter: (value: number) => formatter(value) } }
        }
    )

})

/**
 * reset
 */

const reset = async () => initialDataLoader.reload()
defineExpose({reset})
reset()

</script>
